import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';

interface PriceData {
  symbol: string;
  price: number;
  change24h: number;
  changePercent24h: number;
}

const LivePrices: React.FC = () => {
  const [prices, setPrices] = useState<{ [key: string]: PriceData }>({
    BTC: { symbol: 'BTC', price: 0, change24h: 0, changePercent24h: 0 },
    ETH: { symbol: 'ETH', price: 0, change24h: 0, changePercent24h: 0 },
    SOL: { symbol: 'SOL', price: 0, change24h: 0, changePercent24h: 0 },
    XRP: { symbol: 'XRP', price: 0, change24h: 0, changePercent24h: 0 },
    PAXG: { symbol: 'PAXG', price: 0, change24h: 0, changePercent24h: 0 },
  });

  // Fetch 24h stats from Binance REST API for initial data
  const fetch24hStats = useCallback(async () => {
    try {
      const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'PAXGUSDT'];
      const promises = symbols.map(symbol =>
        fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${symbol}`)
          .then(res => res.json())
      );
      
      const results = await Promise.all(promises);
      
      results.forEach((data) => {
        if (data && data.symbol) {
          const symbol = data.symbol.replace('USDT', '');
          setPrices(prev => ({
            ...prev,
            [symbol]: {
              symbol,
              price: parseFloat(data.lastPrice),
              change24h: parseFloat(data.priceChange),
              changePercent24h: parseFloat(data.priceChangePercent),
            }
          }));
        }
      });
    } catch {
      // Silent fail for 24h stats fetch
    }
  }, []);

  useEffect(() => {
    // Fetch initial data
    fetch24hStats();

    let ws: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout;
    let isComponentMounted = true;

    // Function to connect to WebSocket
    const connectWebSocket = () => {
      if (!isComponentMounted) return;

      try {
        // Binance WebSocket for BTC, ETH, SOL, XRP, PAXG
        const wsUrl = 'wss://stream.binance.com:9443/ws/btcusdt@ticker/ethusdt@ticker/solusdt@ticker/xrpusdt@ticker/paxgusdt@ticker';
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          // Connected successfully
        };

        ws.onmessage = (event) => {
          if (!isComponentMounted) return;
          try {
            const data = JSON.parse(event.data);
            if (data && data.s) {
              const symbol = data.s.replace('USDT', '');
              setPrices(prev => ({
                ...prev,
                [symbol]: {
                  symbol,
                  price: parseFloat(data.c), // current price
                  change24h: parseFloat(data.p), // price change
                  changePercent24h: parseFloat(data.P), // price change percent
                }
              }));
            }
          } catch {
            // Silent fail for parsing errors
          }
        };

        ws.onerror = () => {
          // Silent fail - will reconnect
        };

        ws.onclose = () => {
          if (isComponentMounted) {
            // Attempt to reconnect after 5 seconds
            reconnectTimeout = setTimeout(connectWebSocket, 5000);
          }
        };
      } catch {
        // Silent fail - will reconnect
        if (isComponentMounted) {
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      }
    };

    // Initial connection
    connectWebSocket();

    // Cleanup
    return () => {
      isComponentMounted = false;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [fetch24hStats]);

  const formatPrice = (symbol: string, price: number): string => {
    if (price === 0) return '--';
    if (symbol === 'BTC' || symbol === 'ETH' || symbol === 'PAXG') {
      return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // XRP and SOL - show more decimals for lower priced assets
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  const formatPercent = (percent: number): string => {
    if (percent === 0) return '--';
    return percent >= 0 ? `+${percent.toFixed(2)}%` : `${percent.toFixed(2)}%`;
  };

  return (
    <div className="w-full bg-gradient-to-r from-slate-900/50 to-slate-800/50 border-y border-blue-500/20 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-center gap-4 overflow-x-auto scrollbar-hide">
          {/* Price cards */}
          <div className="flex items-center gap-4 flex-1 justify-center">
            {Object.values(prices).map((asset) => {
              const isPositive = asset.changePercent24h >= 0;
              
              // Define display properties for each symbol including logo URLs
              const getDisplayInfo = (symbol: string) => {
                switch(symbol) {
                  case 'BTC':
                    return { 
                      name: 'BTC', 
                      label: 'Bitcoin',
                      logo: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png'
                    };
                  case 'ETH':
                    return { 
                      name: 'ETH', 
                      label: 'Ethereum',
                      logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png'
                    };
                  case 'SOL':
                    return { 
                      name: 'SOL', 
                      label: 'Solana',
                      logo: 'https://assets.coingecko.com/coins/images/4128/small/solana.png'
                    };
                  case 'XRP':
                    return { 
                      name: 'XRP', 
                      label: 'Ripple',
                      logo: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png'
                    };
                  case 'PAXG':
                    return { 
                      name: 'PAXG', 
                      label: 'Pax Gold',
                      logo: 'https://assets.coingecko.com/coins/images/9519/small/paxgold.png'
                    };
                  default:
                    return { name: symbol, label: 'USD', logo: null };
                }
              };
              
              const displayInfo = getDisplayInfo(asset.symbol);
              
              return (
                <motion.div
                  key={asset.symbol}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center space-x-3 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-blue-500/30 transition-all group"
                >
                  {/* Logo and Symbol */}
                  <div className="flex items-center space-x-2">
                    {displayInfo.logo && (
                      <img 
                        src={displayInfo.logo} 
                        alt={displayInfo.label}
                        className="w-6 h-6 rounded-full"
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors">
                        {displayInfo.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {displayInfo.label}
                      </span>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-semibold text-white mono-text">
                      ${formatPrice(asset.symbol, asset.price)}
                    </span>
                    <div className="flex items-center space-x-1">
                      {isPositive ? (
                        <TrendingUp size={12} className="text-green-400" />
                      ) : (
                        <TrendingDown size={12} className="text-red-400" />
                      )}
                      <span className={`text-xs font-medium mono-text ${
                        isPositive ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {formatPercent(asset.changePercent24h)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
};

export default LivePrices;
